import * as Sentry from '@sentry/browser';
import { initSentryHelpers } from '@zoobook/shared/sentry/breadcrumbs';

// Re-export shared user/error helpers
export {
  setSentryUser,
  clearSentryUser,
  reportErrorBoundary,
} from '@zoobook/shared/sentry/breadcrumbs';

const SENTRY_DSN = process.env.SENTRY_DSN;
const APP_VERSION = process.env.APP_VERSION || '0.0.1';
const NODE_ENV = process.env.NODE_ENV || 'development';
const IS_PRODUCTION = NODE_ENV === 'production';

/**
 * Initialize Sentry for browser error tracking and performance monitoring.
 * Call once at app startup (before React renders).
 */
export function initSentry() {
  if (!SENTRY_DSN) {
    console.warn('Sentry DSN not configured, error tracking disabled');
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: NODE_ENV,
    release: `zoobook-web@${APP_VERSION}`,
    tracesSampleRate: IS_PRODUCTION ? 0.1 : 1.0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: IS_PRODUCTION ? 1.0 : 0,

    beforeSend(event) {
      // Drop HMR hot-update errors in development — transient module refresh artifacts
      if (!IS_PRODUCTION) {
        const frames = event.exception?.values?.[0]?.stacktrace?.frames;
        if (frames?.some((f) => f.filename?.includes('.hot-update.'))) {
          return null;
        }
      }

      if (event.breadcrumbs) {
        for (const bc of event.breadcrumbs) {
          if (bc.data?.authorization) bc.data.authorization = '[REDACTED]';
          if (bc.data?.token) bc.data.token = '[REDACTED]';
        }
      }
      return event;
    },

    ignoreErrors: [
      'top.GLOBALS',
      'originalCreateNotification',
      'canvas.contentDocument',
      'Failed to fetch',
      'NetworkError',
      'Load failed',
      'ResizeObserver loop',
    ],

    denyUrls: [
      /\.hot-update\./i,
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
      /^safari-extension:\/\//i,
    ],

    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.category === 'xhr' && breadcrumb.data?.url?.includes('/health')) {
        return null;
      }
      return breadcrumb;
    },
  });

  // Wire up shared helpers with platform-specific Sentry instance
  initSentryHelpers(Sentry);
}

export { Sentry };
